import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

import { datesAPI, reserveAPI, cash, dateRange, nightsLabel } from "../../services/api";
import { sellerAPI } from "../../services/portalApi";

const blankGuest = () => ({ first_name: "", last_name: "", email: "", phone: "", diets: [], dietary_notes: "" });
const PLAN_NOTE = { full: "Pay in full", deposit: "Deposit now, balance later", plan: "Instalments" };
const METHOD = { bank: "Bank transfer", card: "Card", crypto: "Cryptocurrency" };

/**
 * Booking for a client, on one page: the date, how many, where they stay,
 * who they are, and how they'll pay. The client is emailed what's booked
 * and how to pay; Makua's team assigns the exact room and handles the
 * medical screening.
 */
export default function PartnerBook({ me, onBooked, onCancel }) {
  const [dates, setDates] = useState(null);
  const [dateId, setDateId] = useState("");
  const [guests, setGuests] = useState(1);
  const [before, setBefore] = useState(0);
  const [after, setAfter] = useState(0);
  const [options, setOptions] = useState(null);
  const [roomId, setRoomId] = useState(null);
  const [people, setPeople] = useState([blankGuest()]);
  const [quote, setQuote] = useState(null);
  const [plan, setPlan] = useState("");
  const [method, setMethod] = useState("");
  const [accepted, setAccepted] = useState(false);
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => { datesAPI.list().then((d) => setDates((d.dates || []).filter((x) => !x.soldOut))).catch((e) => setError(e.message)); }, []);
  const date = useMemo(() => (dates || []).find((d) => String(d.id) === String(dateId)), [dates, dateId]);

  // What this date offers for this party
  useEffect(() => {
    if (!dateId) { setOptions(null); return; }
    let alive = true;
    reserveAPI.options(dateId, { guests, before, after })
      .then((o) => { if (alive) { setOptions(o); setError(""); } })
      .catch((e) => { if (alive) { setOptions(null); setError(e.message); } });
    return () => { alive = false; };
  }, [dateId, guests, before, after]);

  // Keep one form per guest
  useEffect(() => {
    setPeople((p) => (p.length === guests ? p : Array.from({ length: guests }, (_, i) => p[i] || blankGuest())));
  }, [guests]);

  const room = options?.categories.find((c) => c.id === roomId) || null;
  useEffect(() => { if (room && !room.fits && !room.requestOnly) setRoomId(null); }, [room]);

  // The price, as the client will see it
  useEffect(() => {
    if (!dateId || !roomId) { setQuote(null); return; }
    let alive = true;
    reserveAPI.quote(dateId, { guests_count: guests, nights_before: before, nights_after: after, room_type_id: roomId, addons: [] })
      .then((q) => {
        if (!alive) return;
        setQuote(q);
        const plans = q.options?.plans || [];
        if (!plans.some((p) => p.id === plan)) setPlan(plans[0]?.id || "");
        const methods = q.options?.methods || [];
        if (!methods.includes(method)) setMethod(methods[0] || "");
      })
      .catch((e) => { if (alive) setError(e.message); });
    return () => { alive = false; };
  }, [dateId, roomId, guests, before, after]); // eslint-disable-line react-hooks/exhaustive-deps

  const setPerson = (i, k, v) => setPeople((p) => p.map((g, j) => (j === i ? { ...g, [k]: v } : g)));
  const toggleDiet = (i, id) => setPeople((p) => p.map((g, j) => {
    if (j !== i) return g;
    const exclusive = new Set((options?.diets || []).filter((d) => d.exclusive).map((d) => d.id));
    const has = g.diets.includes(id);
    const diets = has ? g.diets.filter((x) => x !== id) : exclusive.has(id) ? [id] : [...g.diets.filter((x) => !exclusive.has(x)), id];
    return { ...g, diets };
  }));

  const ext = options?.retreat?.extension;
  const requestOnly = !!room?.requestOnly;
  const lead = people[0] || {};
  const ready = date && room && (room.fits || requestOnly) && quote &&
    people.every((g) => g.first_name.trim() && g.last_name.trim()) && /\S+@\S+\.\S+/.test(lead.email) &&
    (requestOnly || (plan && method)) && accepted;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      const r = await sellerAPI.book({
        pathway_id: Number(dateId), guests_count: guests, nights_before: before, nights_after: after,
        room_type_id: roomId, addons: [], guests: people,
        payment_plan: requestOnly ? undefined : plan, payment_method: requestOnly ? undefined : method,
        special_requests: notes || undefined, accept_terms: accepted,
      });
      onBooked(r);
    } catch (err) {
      setError(err.message);
      // Something may have gone while they were filling in: refresh what's left
      reserveAPI.options(dateId, { guests, before, after }).then(setOptions).catch(() => {});
    } finally { setBusy(false); }
  };

  return (
    <div className="mk-wrap mk-wrap--wide">
      <button type="button" className="pt-back" onClick={onCancel}>← Your bookings</button>
      <p className="mk-eyebrow">Book for a client</p>
      <h1 className="mk-title">New booking</h1>
      <p className="mk-muted mk-sub">We email your client what's booked and how to pay, and hold their place while they do. We assign the exact room and look after their medical screening.</p>

      <form className="pt-book" onSubmit={submit}>
        <fieldset className="mk-card pt-step">
          <legend className="pt-step-title">Retreat and dates</legend>
          <label className="mk-field">
            <span>Retreat</span>
            <select value={dateId} onChange={(e) => { setDateId(e.target.value); setRoomId(null); setBefore(0); setAfter(0); }} required>
              <option value="">{dates ? "Choose a date…" : "Loading…"}</option>
              {(dates || []).map((d) => (
                <option key={d.id} value={d.id}>{d.title} · {dateRange(d.startDate, d.endDate)} · {d.placesLeft} {d.placesLeft === 1 ? "place" : "places"} left</option>
              ))}
            </select>
          </label>
          {date && (
            <div className="mk-row">
              <label className="mk-field">
                <span>Guests</span>
                <select value={guests} onChange={(e) => setGuests(Number(e.target.value))}>
                  {Array.from({ length: Math.min(options?.retreat?.maxParty || 6, date.placesLeft) }, (_, i) => i + 1).map((n) => <option key={n} value={n}>{n}</option>)}
                </select>
              </label>
              {ext?.maxBefore > 0 && (
                <label className="mk-field">
                  <span>Extra nights before <em>{cash(ext.nightPrice)} a night each</em></span>
                  <select value={before} onChange={(e) => setBefore(Number(e.target.value))}>
                    {Array.from({ length: ext.maxBefore + 1 }, (_, i) => <option key={i} value={i}>{i}</option>)}
                  </select>
                </label>
              )}
              {ext?.maxAfter > 0 && (
                <label className="mk-field">
                  <span>Extra nights after</span>
                  <select value={after} onChange={(e) => setAfter(Number(e.target.value))}>
                    {Array.from({ length: ext.maxAfter + 1 }, (_, i) => <option key={i} value={i}>{i}</option>)}
                  </select>
                </label>
              )}
            </div>
          )}
          {options && <p className="mk-muted mb-0">Arrive {options.selection.arrive}, leave {options.selection.depart}.</p>}
        </fieldset>

        {options && (
          <fieldset className="mk-card pt-step">
            <legend className="pt-step-title">Where they stay</legend>
            <div className="pt-rooms" role="radiogroup" aria-label="Room type">
              {options.categories.map((c) => {
                const off = !c.fits && !c.requestOnly;
                return (
                  <label key={c.id} className={`pt-room ${roomId === c.id ? "is-on" : ""} ${off ? "is-off" : ""}`}>
                    <input type="radio" name="room" className="visually-hidden" checked={roomId === c.id} disabled={off} onChange={() => setRoomId(c.id)} />
                    <span className="pt-room-name">{c.name}</span>
                    <span className="pt-room-meta">{c.summary || c.group}</span>
                    <span className="pt-room-price">
                      {c.requestOnly ? "By request" : off ? "Not available" : c.isBase || !c.upgradeForStay ? "Included" : `+${cash(c.upgradeForStay)}`}
                    </span>
                  </label>
                );
              })}
            </div>
          </fieldset>
        )}

        {room && (
          <fieldset className="mk-card pt-step">
            <legend className="pt-step-title">Guests</legend>
            {people.map((g, i) => (
              <div key={i} className="pt-guest">
                <p className="bk-label">{i === 0 ? "Lead guest (your client)" : `Guest ${i + 1}`}</p>
                <div className="mk-row">
                  <label className="mk-field"><span>First name</span><input value={g.first_name} onChange={(e) => setPerson(i, "first_name", e.target.value)} required /></label>
                  <label className="mk-field"><span>Last name</span><input value={g.last_name} onChange={(e) => setPerson(i, "last_name", e.target.value)} required /></label>
                </div>
                <div className="mk-row">
                  <label className="mk-field"><span>Email {i === 0 ? "" : <em>optional</em>}</span><input type="email" value={g.email} onChange={(e) => setPerson(i, "email", e.target.value)} required={i === 0} /></label>
                  <label className="mk-field"><span>Phone <em>optional</em></span><input value={g.phone} onChange={(e) => setPerson(i, "phone", e.target.value)} /></label>
                </div>
                <p className="bk-label pt-diet-label">Food <em>select all that apply</em></p>
                <div className="bk-chips">
                  {(options.diets || []).map((d) => (
                    <label key={d.id} className={`bk-chip ${g.diets.includes(d.id) ? "is-on" : ""}`}>
                      <input type="checkbox" checked={g.diets.includes(d.id)} onChange={() => toggleDiet(i, d.id)} />{d.name}
                    </label>
                  ))}
                </div>
                <label className="mk-field mt-2"><span>Allergies and dietary needs <em>optional</em></span>
                  <textarea rows={2} value={g.dietary_notes} onChange={(e) => setPerson(i, "dietary_notes", e.target.value)} maxLength={1000} />
                </label>
              </div>
            ))}
            <label className="mk-field"><span>Anything we should know <em>optional</em></span>
              <textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} maxLength={2000} />
            </label>
          </fieldset>
        )}

        {quote && (
          <fieldset className="mk-card pt-step">
            <legend className="pt-step-title">Price and payment</legend>
            <table className="bk-lines">
              <tbody>{quote.lines.map((l, i) => <tr key={i}><th scope="row">{l.label}</th><td>{l.amount === 0 ? "Included" : cash(l.amount)}</td></tr>)}</tbody>
              <tfoot><tr className="bk-total"><th scope="row">Total</th><td>{cash(quote.total)}</td></tr></tfoot>
            </table>
            {requestOnly ? (
              <p className="mk-muted">This room is by request: we'll confirm availability and the price with your client before anything is paid.</p>
            ) : (
              <div className="mk-row">
                <label className="mk-field">
                  <span>How your client pays</span>
                  <select value={plan} onChange={(e) => setPlan(e.target.value)}>
                    {quote.options.plans.map((p) => <option key={p.id} value={p.id}>{PLAN_NOTE[p.id] || p.label}: {cash(p.dueNow)} now</option>)}
                  </select>
                </label>
                <label className="mk-field">
                  <span>Method</span>
                  <select value={method} onChange={(e) => setMethod(e.target.value)}>
                    {(quote.options.methods || []).map((m) => <option key={m} value={m}>{METHOD[m] || m}</option>)}
                  </select>
                </label>
              </div>
            )}
            {me.seller.commission_mode !== "none" && (
              <p className="pt-earn">Your commission on this booking: <b>{cash(me.seller.commission_mode === "percent" ? (quote.total * me.seller.commission_value) / 100 : me.seller.commission_value * guests)}</b>, once it's confirmed.</p>
            )}
            <label className="pt-accept">
              <input type="checkbox" checked={accepted} onChange={(e) => setAccepted(e.target.checked)} />
              <span>My client has read and accepted the <Link to="/terms" target="_blank">terms</Link> and the <Link to="/cancellation-policy" target="_blank">cancellation policy</Link>.</span>
            </label>
          </fieldset>
        )}

        {error && <p className="mk-error" role="alert">{error}</p>}
        <div className="mk-actions">
          <button className="mk-btn mk-btn--primary" disabled={!ready || busy}>
            {busy ? "Booking…" : requestOnly ? "Send request" : `Book ${guests === 1 ? "1 place" : `${guests} places`}`}
          </button>
          <button type="button" className="mk-btn mk-btn--ghost" onClick={onCancel}>Cancel</button>
        </div>
        {date && <p className="mk-muted pt-small">{date.title}: {nightsLabel(date.nights)}.</p>}
      </form>
    </div>
  );
}
